import { defineStore } from "pinia";
import type { CellTypeMenuOption } from "@/utils/constants/cellTypeMenuConfig";
import type {
  Annotation,
  AnnotationImagePayload,
  AnnotationLabelFilter,
  AnnotationSchemaPayload,
} from "@/components/RegionBasedAnnotation/types/annotation";

type RBCSlideState = {
  selectedLabel: string | undefined;
  selectedName: string | undefined;
  currentSelectedSlide: string | null;
  images: AnnotationSchemaPayload;
  labelFilter: AnnotationLabelFilter;
};

const initState = (): RBCSlideState => ({
  selectedLabel: 'Normal RBC',
  selectedName: 'Normal RBC',
  currentSelectedSlide: null,
  images: {},
  labelFilter: {},
});

export const useRBCSlideStore = defineStore("RBCslide", {
  state: initState,
  actions: {
    setSelectedType(option: CellTypeMenuOption | null) {
      this.selectedLabel = option ? option.label ?? option.name ?? undefined : undefined;
      this.selectedName = option ? option.name ?? undefined : undefined;
    },
    setCurrentSlide(slideId: string | null) {
      this.currentSelectedSlide = slideId;
      this.syncLabelFilter(slideId ?? undefined);
    },
    loadSchema(payload: AnnotationSchemaPayload) {
      if (!payload || Object.keys(payload).length === 0) {
        this.images = {};
        this.currentSelectedSlide = null;
      } else {
        this.images = { ...payload };
        const keys = Object.keys(this.images);
        this.currentSelectedSlide = keys.length > 0 ? keys[0] : null;
      }
      this.syncLabelFilter();
    },
    serializeSchema(): AnnotationSchemaPayload {
      return { ...this.images };
    },
    upsertImage(id: string, next: AnnotationImagePayload) {
      this.images = {
        ...this.images,
        [id]: next,
      };
      this.syncLabelFilter();
    },
    updateAnnotations(imageId: string, annotations: Annotation[]) {
      if (!this.images[imageId]) {
        return;
      }
      const clone = { ...this.images };
      clone[imageId] = { ...clone[imageId], annotations };
      this.images = clone;
      this.syncLabelFilter();
    },
    setLabelFilterValue(label: string, checked: boolean) {
      this.labelFilter = { ...this.labelFilter, [label]: checked };
    },
    setLabelFilter(map: AnnotationLabelFilter) {
      this.labelFilter = { ...map };
    },
    syncLabelFilter(targetSlideId?: string | null) {
      const activeSlideId = targetSlideId ?? this.currentSelectedSlide;
      const activeSlide = activeSlideId
        ? this.images[activeSlideId] ?? null
        : null;
      this.labelFilter = deriveLabelFilter(activeSlide, this.labelFilter);
    },
    deleteSlide(id: string) {
      if (!this.images[id]) return;
      
      this.images[id] = {
        ...this.images[id],
        is_deleted: true,
        deleted_at: new Date().toISOString(),
      };

      // Auto-advance if the deleted slide was selected
      if (this.currentSelectedSlide === id) {
        const nextId = this.findNextSelectableSlideId(id);
        this.setCurrentSlide(nextId);
      }
    },
    findNextSelectableSlideId(currentId: string): string | null {
      const keys = Object.keys(this.images);
      const currentIndex = keys.indexOf(currentId);
      if (currentIndex === -1) return null;

      // 1. Search forward for next available non-deleted slide
      for (let i = currentIndex + 1; i < keys.length; i++) {
        const slide = this.images[keys[i]];
        if (!slide.is_deleted) return keys[i];
      }
      // 2. Search backward if no forward slide found
      for (let i = currentIndex - 1; i >= 0; i--) {
        const slide = this.images[keys[i]];
        if (!slide.is_deleted) return keys[i];
      }
      return null;
    },
    restoreSlide(id: string) {
      if (!this.images[id]) return;
      this.images[id] = {
        ...this.images[id],
        is_deleted: false,
      };
    },
    reset() {      
      this.$reset();
    },
  },
});

function deriveLabelFilter(
  slide: AnnotationImagePayload | null,
  previous: AnnotationLabelFilter = {},
): AnnotationLabelFilter {
  if (!slide) {
    return {};
  }
  const next: AnnotationLabelFilter = {};
  slide.annotations.forEach((annotation) => {
    const label = annotation.label;
    if (!label || next[label] !== undefined) return;
    next[label] = previous[label] ?? true;
  });
  return next;
}

export default useRBCSlideStore;
